import {
  faCheckCircle,
  faExclamationCircle,
  faInfoCircle,
  faTimes,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ReactNode, useState } from 'react';

import { cn } from '../lib/utils';

type AlertVariant = 'info' | 'success' | 'warning' | 'error' | 'neutral';

const alertConfig: Record<
  AlertVariant,
  { bar: string; bg: string; text: string; icon: typeof faInfoCircle; iconColor: string }
> = {
  info: {
    bar: 'bg-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    text: 'text-blue-800 dark:text-blue-200',
    icon: faInfoCircle,
    iconColor: 'text-blue-500 dark:text-blue-400'
  },
  success: {
    bar: 'bg-green-500',
    bg: 'bg-green-50 dark:bg-green-950/30',
    text: 'text-green-800 dark:text-green-200',
    icon: faCheckCircle,
    iconColor: 'text-green-700 dark:text-green-400'
  },
  warning: {
    bar: 'bg-yellow-400',
    bg: 'bg-yellow-50 dark:bg-yellow-950/30',
    text: 'text-yellow-800 dark:text-yellow-200',
    icon: faExclamationCircle,
    iconColor: 'text-yellow-700 dark:text-yellow-400'
  },
  error: {
    bar: 'bg-red-500',
    bg: 'bg-red-50 dark:bg-red-950/30',
    text: 'text-red-800 dark:text-red-200',
    icon: faTimesCircle,
    iconColor: 'text-red-500 dark:text-red-400'
  },
  neutral: {
    bar: 'bg-uniform-gray-400',
    bg: 'bg-uniform-gray-50 dark:bg-uniform-gray-800',
    text: 'text-uniform-gray-700 dark:text-uniform-gray-300',
    icon: faInfoCircle,
    iconColor: 'text-uniform-gray-500 dark:text-uniform-gray-400'
  }
};

interface AlertProps {
  variant?: AlertVariant;
  children?: ReactNode;
  message?: string;
  dismissable?: boolean;
  onDismiss?: () => void;
  className?: string;
}

function Alert({
  variant = 'neutral',
  children,
  message,
  dismissable = false,
  onDismiss,
  className
}: AlertProps) {
  const [visible, setVisible] = useState(true);
  if (!visible) return null;

  const { bar, bg, text, icon, iconColor } = alertConfig[variant];

  return (
    <div role="alert" className={cn('flex overflow-hidden rounded-lg', bg, className)}>
      <div className={cn('w-1 shrink-0', bar)} />
      <div className="flex flex-1 items-start gap-3 px-4 py-3">
        <FontAwesomeIcon
          icon={icon}
          className={cn('mt-0.5 h-4 w-4 shrink-0', iconColor)}
          aria-hidden="true"
        />
        <div className={cn('flex-1 text-sm', text)}>{message ?? children}</div>
        {dismissable && (
          <button
            type="button"
            onClick={() => {
              setVisible(false);
              onDismiss?.();
            }}
            aria-label="Dismiss"
            className={cn(
              'shrink-0 opacity-60 transition-opacity hover:opacity-100',
              '[outline:none] focus-visible:ring-1 focus-visible:ring-current rounded',
              text
            )}>
            <FontAwesomeIcon icon={faTimes} className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export { Alert };
export type { AlertProps, AlertVariant };
